import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';

const validateUserId = (userId) => {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    throw new Error('User ID is required and must be a valid string');
  }
  return userId.trim();
};

const validateDateRange = (dateRange) => {
  if (dateRange === null || dateRange === undefined) {
    return null;
  }

  if (typeof dateRange !== 'object') {
    throw new Error('Date range must be a valid object');
  }

  const validatedRange = {};

  if (dateRange.startDate) {
    if (!(dateRange.startDate instanceof Date) || isNaN(dateRange.startDate.getTime())) {
      throw new Error('Start date must be a valid Date object');
    }
    validatedRange.startDate = dateRange.startDate;
  }

  if (dateRange.endDate) {
    if (!(dateRange.endDate instanceof Date) || isNaN(dateRange.endDate.getTime())) {
      throw new Error('End date must be a valid Date object');
    }
    validatedRange.endDate = dateRange.endDate;
  }

  if (validatedRange.startDate && validatedRange.endDate) {
    if (validatedRange.endDate < validatedRange.startDate) {
      throw new Error('End date cannot be before start date');
    }
  }

  return validatedRange;
};

const validateInsightsType = (type) => {
  if (!type || typeof type !== 'string' || type.trim() === '') {
    throw new Error('Insights type is required and must be a valid string');
  }

  const validTypes = ['leads', 'views', 'link-taps', 'recent-activity'];
  const validType = type.trim().toLowerCase();

  if (!validTypes.includes(validType)) {
    throw new Error(`Insights type must be one of: ${validTypes.join(', ')}`);
  }

  return validType;
};

const handleInsightsAuthError = (error, operation = 'API operation') => {
  if (error.response?.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    throw new Error('Your session has expired. Please log in again');
  }

  if (error.response?.status === 403) {
    throw new Error('You do not have permission to access insights data');
  }

  if (error.response?.status === 404) {
    throw new Error('Insights data not found or user does not exist');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid insights parameters provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status >= 500) {
    throw new Error('Insights service error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
  throw new Error(errorMessage);
};

const buildQueryParams = (dateRange) => {
  const params = new URLSearchParams();

  if (dateRange && dateRange.startDate) {
    params.append('startDate', dateRange.startDate.toISOString());
  }

  if (dateRange && dateRange.endDate) {
    params.append('endDate', dateRange.endDate.toISOString());
  }

  return params;
};

export const fetchInsightsData = async (userId, dateRange = null) => {
  try {
    const validUserId = validateUserId(userId);
    const validDateRange = validateDateRange(dateRange);

    const params = buildQueryParams(validDateRange);

    const response = await apiClient.get(`${API_BASE_URL}/api/insights/${validUserId}?${params.toString()}`);

    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error('Failed to fetch insights data');
    }
  } catch (error) {
    handleInsightsAuthError(error, 'Fetching insights data');
  }
};

export const fetchLeadsInsights = async (userId, dateRange = null) => {
  try {
    const validUserId = validateUserId(userId);
    const validDateRange = validateDateRange(dateRange);

    const params = buildQueryParams(validDateRange);

    const response = await apiClient.get(`${API_BASE_URL}/api/insights/${validUserId}/leads?${params.toString()}`);

    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error('Failed to fetch leads insights data');
    }
  } catch (error) {
    handleInsightsAuthError(error, 'Fetching leads insights');
  }
};

export const fetchViewsInsights = async (userId, dateRange = null) => {
  try {
    const validUserId = validateUserId(userId);
    const validDateRange = validateDateRange(dateRange);

    const params = buildQueryParams(validDateRange);

    const response = await apiClient.get(`${API_BASE_URL}/api/insights/${validUserId}/views?${params.toString()}`);

    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error('Failed to fetch views insights data');
    }
  } catch (error) {
    handleInsightsAuthError(error, 'Fetching views insights');
  }
};

export const fetchLinkTapsInsights = async (userId, dateRange = null) => {
  try {
    const validUserId = validateUserId(userId);
    const validDateRange = validateDateRange(dateRange);

    const params = buildQueryParams(validDateRange);

    const response = await apiClient.get(`${API_BASE_URL}/api/insights/${validUserId}/link-taps?${params.toString()}`);

    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error('Failed to fetch link taps insights data');
    }
  } catch (error) {
    handleInsightsAuthError(error, 'Fetching link taps insights');
  }
};

export const fetchRecentActivity = async (userId, dateRange = null) => {
  try {
    const validUserId = validateUserId(userId);
    const validDateRange = validateDateRange(dateRange);

    const params = buildQueryParams(validDateRange);

    const response = await apiClient.get(`${API_BASE_URL}/api/insights/${validUserId}/recent-activity?${params.toString()}`);

    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error('Failed to fetch recent activity data');
    }
  } catch (error) {
    handleInsightsAuthError(error, 'Fetching recent activity');
  }
};